import { useRef, useState } from 'react';
import { Logo } from './Logo';
import { appPath } from '../lib/config';
import './marketing-home.css';
import './marketing-chrome.css';

export function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14m-6-6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

export function MarketingHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const path = window.location.pathname.replace(/\/+$/, '');
  return (
    <header
      className="site-header"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && menuOpen) {
          setMenuOpen(false);
          menuButton.current?.focus();
        }
      }}
    >
      <div className="mh-shell site-header__inner">
        <Logo />
        <button
          className="site-menu-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="site-navigation"
          ref={menuButton}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span>{menuOpen ? 'Close' : 'Menu'}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d={menuOpen ? 'M6 6l12 12M6 18 18 6' : 'M4 7h16M4 12h16M4 17h16'} />
          </svg>
        </button>
        <div
          id="site-navigation"
          className={`site-header__navigation${menuOpen ? ' is-open' : ''}`}
        >
          <nav
            className="site-nav"
            aria-label="Primary navigation"
            onClick={() => setMenuOpen(false)}
          >
            <a href="/#product">Platform</a>
            <a href="/paddiq-link" aria-current={path === '/paddiq-link' ? 'page' : undefined}>
              Paddiq Link <span className="site-coming-soon">Beta</span>
            </a>
            <a href="/#pricing">Pricing</a>
            <a href="/resources" aria-current={path.startsWith('/resources') ? 'page' : undefined}>
              Resources
            </a>
          </nav>
          <div className="site-header__actions">
            <a className="site-login" href={appPath('/login')}>
              Log in
            </a>
            <a className="mh-button mh-button--small" href={appPath('/register')}>
              Get started <ArrowIcon />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer className="site-footer">
      <div className="mh-shell">
        <div className="site-footer__invitation">
          <div>
            <p className="site-footer__eyebrow">Try Paddiq with your own data</p>
            <h2>Review your next session in Paddiq.</h2>
          </div>
          <a className="mh-button" href={appPath('/register')}>
            Create your account <ArrowIcon />
          </a>
        </div>
        <div className="site-footer__grid">
          <div className="site-footer__brand">
            <Logo inverted />
            <p>
              Race data and session analysis
              <br />
              for drivers, coaches, and teams.
            </p>
            <a className="site-footer__contact" href="/support">
              Talk to the Paddiq team <ArrowIcon />
            </a>
          </div>
          <nav aria-label="Footer products">
            <h3>Products</h3>
            <a href="/#product">Paddiq platform</a>
            <a href="/paddiq-link">
              Paddiq Link <span className="site-coming-soon">Beta</span>
            </a>
            <a href="/chatgpt">
              ChatGPT plugin <span className="site-coming-soon">Coming soon</span>
            </a>
            <a href="/#pricing">Pricing</a>
            <a
              href="https://apps.apple.com/sa/app/paddiq/id6785660120"
              target="_blank"
              rel="noreferrer"
            >
              Paddiq for iPhone ↗
            </a>
          </nav>
          <nav aria-label="Footer resources">
            <h3>Resources</h3>
            <a href="/resources/getting-started">Getting started</a>
            <a href="/resources/paddiq-link">Paddiq Link guide</a>
            <a href="/resources/upload-mychron-session">Import your sessions</a>
            <a href="/resources/compare-laps-and-traces">Compare laps</a>
            <a href="/resources/product-updates">Product updates</a>
          </nav>
          <nav aria-label="Footer company">
            <h3>Connect</h3>
            <a href="/support">Support</a>
            <a href="https://www.instagram.com/paddiq.app/" target="_blank" rel="noreferrer">
              Instagram ↗
            </a>
            <a href="mailto:support@paddiq.app">Email us</a>
            <a href="/privacy">Privacy policy</a>
            <a href="/terms">Service information</a>
          </nav>
        </div>
        <div className="site-footer__bottom">
          <span>© {new Date().getFullYear()} Paddiq LLC</span>
          <span>Performance. Data. Edge.</span>
        </div>
      </div>
    </footer>
  );
}
